import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { ERouteControlViewType, Map } from '@cian/frontend-newbuilding-map-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  trackBuildRouteClose,
  trackFullscreenClose,
  trackFullscreenShow,
  trackInfrastructureClick,
  trackInfrastructureShow,
  trackPanoramaClose,
  trackPanoramaShow,
  trackRulerClick,
  trackYearClick,
} from './analytics';
import {
  useGeoObjects,
  useGeoObjectBalloon,
  useFutureInfrastructure,
  useFutureTypes,
  useInfrastructureTypesState,
  useSelectedYear,
  useSetGeoObjectsCallbacks,
  useRouting,
} from './hooks';
import { MapServicesContainer, TransportAccessibilitySidebarContainer } from './parts';
import { getIsDistanceCalculatingEnabled, getMapRouteInfoComponent, getMapYears, getZoom } from './utils';
import { InfrastructureMapTypes } from '../../components/InfrastructureMapTypes';
import { InfrastructureMapYears } from '../../components/InfrastructureMapYears';
import { useFutureMapOnboarding } from '../../components/InfrastructureMapYears/hooks';
import { InfrastructureMapYearsService } from '../../components/InfrastructureMapYearsService';
import { NewbuildingMapWrapper } from '../../components/NewbuildingMapWrapper';
import { RouteButton } from '../../components/RouteButton';
import { useMapOnboardingState } from '../../components/RouteButton/hooks';
import { ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES } from '../../constants/infrastructure';
import { selectIsOfferPageRedesign } from '../../selectors';
import { getIsMapServicesVisible } from '../../selectors/newbuildingMap';
import { selectGeoCoordinates } from '../../selectors/offerData/offer';
import { EMapService, TRegularInfrastructureType } from '../../types/map';
import { useApplicationContext } from '../ApplicationContext';
import { NewMapContainer } from '../NewMapContainer';
import { TransportAccessibilityRateContainer } from '../TransportAccessibilityRate';

export function NewbuildingMapContainer() {
  const { config } = useApplicationContext();
  const isDistanceCalculatingEnabled = getIsDistanceCalculatingEnabled(config);
  const years = React.useMemo(getMapYears, []);
  const pinCoordinates = useSelector(selectGeoCoordinates);
  const redesignPage = useSelector(selectIsOfferPageRedesign);

  const isMapServicesVisible = useSelector(getIsMapServicesVisible);

  const {
    infrastructureByYearRef,
    isInfrastructureFetched,
    infrastructureTypes,
    onGeoObjectsFirstFetch,
    transportAccessibilityInfrastructureByTypeRef,
  } = useGeoObjects();
  const { handleSelectYear, selectedYear, nextAvailableYear } = useSelectedYear(infrastructureByYearRef);
  const [futureTypes, setFutureTypes] = useFutureTypes(infrastructureByYearRef);
  const [isOnboardingOpen, closeOnboarding] = useMapOnboardingState();
  const { isFutureMapOnboardingOpen, closeFutureMapOnboarding, handleFutureMapOnboardingShowing } =
    useFutureMapOnboarding();
  const hasFutureInfrastructure = useFutureInfrastructure(infrastructureByYearRef);

  const mapRef = React.useRef<Map>(null);
  const infrastructureTypeRef = React.useRef<string | undefined>();

  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const [mapService, setMapService] = React.useState<EMapService | null>(null);

  const [currentTypes, setCurrentTypes] = useInfrastructureTypesState(infrastructureTypes);

  const { resetTARouting, startBuildingRoute, handleRouteBuildingSuccess, handleBalloonRouteButtonClick } = useRouting({
    hasFutureInfrastructure,
    closeOnboarding,
    mapApi: mapRef.current,
    regularInfrastructureType: infrastructureTypeRef.current,
    setMapService,
  });

  const { setRegularGeoObjects, setTransportAccessibilityGeoObjects } = useSetGeoObjectsCallbacks(
    mapRef.current,
    infrastructureByYearRef.current,
    transportAccessibilityInfrastructureByTypeRef.current,
    currentTypes,
    selectedYear,
    resetTARouting,
  );

  // TODO: Часть переменных удалится при выпиле фича-флага в NB-1172.
  const isYearsTimelineAvailable = hasFutureInfrastructure && !isMapServicesVisible;
  const isYearsServiceAvailable = hasFutureInfrastructure && mapService === EMapService.FutureInfrastructure;
  const yearsVisible = isYearsTimelineAvailable || isYearsServiceAvailable;

  React.useEffect(() => {
    if (isInfrastructureFetched) {
      setFutureTypes(yearsVisible ? selectedYear : null);
    }
  }, [selectedYear, isInfrastructureFetched, yearsVisible]);

  React.useEffect(() => {
    if (isMapLoaded && isInfrastructureFetched) {
      setRegularGeoObjects(currentTypes, selectedYear, !yearsVisible);
    }
  }, [currentTypes, selectedYear, yearsVisible, isInfrastructureFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  const setInfrastructureType = React.useCallback((types: string[]) => {
    infrastructureTypeRef.current = types.find(type => !ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES.includes(type));
    setCurrentTypes(types);
    trackInfrastructureClick(infrastructureTypeRef.current);
  }, []);

  const handleServiceClick = React.useCallback(
    (serviceType: EMapService | null) => {
      setMapService(serviceType);

      if (serviceType === EMapService.Routes) {
        startBuildingRoute(undefined, ERouteControlViewType.stickyLeft);
      }
    },
    [startBuildingRoute],
  );

  const handleFullscreenEnter = React.useCallback(() => {
    trackFullscreenShow(infrastructureTypeRef.current);
  }, []);

  const handleFullscreenExit = React.useCallback(() => {
    trackFullscreenClose(infrastructureTypeRef.current);
  }, []);

  const handleMapLoaded = React.useCallback(() => setIsMapLoaded(true), []);

  const handleRouteModeExit = React.useCallback(() => {
    trackBuildRouteClose();

    if (isMapServicesVisible) {
      setMapService(null);
    }
  }, [isMapServicesVisible]);

  const handlePanoramaSuccessEnter = React.useCallback(() => {
    trackPanoramaShow(infrastructureTypeRef.current);
  }, []);

  const handleNoPanoramaEnter = React.useCallback(() => {
    trackPanoramaShow(infrastructureTypeRef.current);
  }, []);

  const handlePanoramaExit = React.useCallback(() => {
    trackPanoramaClose(infrastructureTypeRef.current);
  }, []);

  const handleYearChange = React.useCallback(
    (selectedYear: number) => {
      const selectedInfrastructureType = infrastructureTypeRef.current as TRegularInfrastructureType | undefined;
      trackYearClick({ year: selectedYear, objectType: selectedInfrastructureType });
      handleSelectYear(selectedYear);
    },
    [infrastructureTypeRef, handleSelectYear],
  );

  const handleRulerClick = React.useCallback(() => {
    trackRulerClick(infrastructureTypeRef.current);
  }, []);

  const { onBalloonHtmlFirstFetch, onBalloonOpen } = useGeoObjectBalloon({
    infrastructureByYearRef,
    transportAccessibilityInfrastructureByTypeRef,
    onRouteButtonClick: handleBalloonRouteButtonClick,
    resetTARouting,
  });

  if (!pinCoordinates) {
    return null;
  }

  return (
    <ActionAfterViewed callback={trackInfrastructureShow} percentVisible={0.5}>
      <NewbuildingMapWrapper redesignPage={redesignPage}>
        <NewMapContainer
          mapRef={mapRef}
          mapRouteInfoComponent={getMapRouteInfoComponent(mapService)}
          onGeoObjectsFirstFetch={onGeoObjectsFirstFetch}
          onFullscreenEnter={handleFullscreenEnter}
          onFullscreenExit={handleFullscreenExit}
          onBalloonOpen={onBalloonOpen}
          onPanoramaSuccessEnter={handlePanoramaSuccessEnter}
          onBalloonHtmlFirstFetch={onBalloonHtmlFirstFetch}
          onMapLoaded={handleMapLoaded}
          onNoPanoramaEnter={handleNoPanoramaEnter}
          onPanoramaExit={handlePanoramaExit}
          onRoutingEnd={handleRouteModeExit}
          onRouteBuildingSuccess={handleRouteBuildingSuccess}
          onRulerClick={handleRulerClick}
          id="infrastructure-map"
          renderSlots={({ isPanorama, isRouteMode }) => {
            const isInfrastructureMode =
              !isPanorama && !isRouteMode && mapService !== EMapService.TransportAccessibility;
            const isRouteButtonVisible = isInfrastructureMode && isDistanceCalculatingEnabled && !isMapServicesVisible;
            const isRegularInfrastructureChipsVisible = isInfrastructureMode && infrastructureTypes;

            // TODO: Переменные ниже будут удалены при выпиле фича-флага в NB-1172.
            const isMapServiceMenuVisible = isMapServicesVisible && mapService === null && !isPanorama;
            const isTransportAccessibilityServiceVisible =
              mapService === EMapService.TransportAccessibility && !isPanorama;

            return [
              isRouteButtonVisible && (
                <RouteButton
                  key="route-button"
                  onClick={startBuildingRoute}
                  isOnboardingOpen={isOnboardingOpen}
                  onOnboardingClose={closeOnboarding}
                  withOnboarding
                />
              ),
              isRegularInfrastructureChipsVisible && (
                <InfrastructureMapTypes
                  key="infrastructure-map-types"
                  types={infrastructureTypes}
                  currentTypes={currentTypes}
                  onTypesChange={setInfrastructureType}
                  futureTypes={futureTypes}
                />
              ),
              isYearsTimelineAvailable && !isPanorama && (
                <InfrastructureMapYears
                  key="infrastructure-map-years"
                  nextAvailableYear={nextAvailableYear}
                  selectedYear={selectedYear}
                  years={years}
                  onChange={handleYearChange}
                  isOnboardingOpen={isFutureMapOnboardingOpen}
                  onOnboardingClose={closeFutureMapOnboarding}
                  onOnboardingShowing={handleFutureMapOnboardingShowing}
                />
              ),
              isYearsServiceAvailable && !isPanorama && (
                <InfrastructureMapYearsService
                  key="infrastructure-map-years-service"
                  nextAvailableYear={nextAvailableYear}
                  selectedYear={selectedYear}
                  years={years}
                  onChange={handleYearChange}
                  onClose={() => setMapService(null)}
                />
              ),
              isMapServiceMenuVisible && (
                <MapServicesContainer key="map-services" onServiceClick={handleServiceClick} />
              ),
              isTransportAccessibilityServiceVisible && (
                <TransportAccessibilitySidebarContainer
                  key="transport-accessibility-sidebar"
                  onTransportAccessibilityChange={setTransportAccessibilityGeoObjects}
                  onClose={() => setMapService(null)}
                />
              ),
            ].filter(Boolean);
          }}
          geoObjectNames={currentTypes}
          zoom={getZoom(currentTypes.length)}
        />
        <TransportAccessibilityRateContainer />
      </NewbuildingMapWrapper>
    </ActionAfterViewed>
  );
}

// eslint-disable-next-line import/no-default-export
export default NewbuildingMapContainer;
